import type { TDocumentDefinitions } from 'pdfmake/interfaces'
import { pdfFonts } from './pdfFonts'

// pdfmake's `download` is asynchronous and has no error channel: it re-throws
// internal failures from inside a promise handler, so they surface as an
// unhandled rejection and the completion callback simply never fires. Waiting
// on it unbounded is what left the button spinning forever, so give up after a
// while and tell the user instead. Generous enough for a long care plan.
export const DOWNLOAD_TIMEOUT_MS = 60000

/**
 * Makes a PDF with pdfmake and the bundled fonts and downloads it, resolving
 * once the file is handed to the browser and rejecting on a failure or after
 * `timeoutMs`.
 */
export const downloadPdf = async (
  docDefinition: TDocumentDefinitions,
  fileName: string,
  timeoutMs = DOWNLOAD_TIMEOUT_MS
): Promise<void> => {
  // The fonts are ~700kB of base64, so they are loaded lazily alongside
  // pdfmake itself rather than being pulled into the main bundle.
  const [pdfMake, vfs] = await Promise.all([
    import('pdfmake/build/pdfmake').then((module) => module.default),
    import('./pdfMakeFonts').then((module) => module.default)
  ])

  let timeout: ReturnType<typeof setTimeout> | undefined
  try {
    await new Promise<void>((resolve, reject) => {
      timeout = setTimeout(() => {
        reject(new Error('Timed out generating the PDF'))
      }, timeoutMs)
      pdfMake
        .createPdf(docDefinition, undefined, pdfFonts, vfs)
        .download(fileName, resolve)
    })
  } finally {
    clearTimeout(timeout)
  }
}
